import { useState, useEffect, useRef } from 'react';

interface UseCountUpOptions {
  end: number;
  duration?: number;
  delay?: number;
  /** When true the counter starts animating; stays at 0 until triggered. */
  trigger?: boolean;
}

export function useCountUp({ end, duration = 2000, delay = 0, trigger = true }: UseCountUpOptions) {
  const [count, setCount] = useState(0);
  const hasStartedRef = useRef(false);
  const frameRef = useRef<number>();

  useEffect(() => {
    if (!trigger || hasStartedRef.current) return;
    hasStartedRef.current = true;

    let timer: ReturnType<typeof setTimeout>;

    const runAnimation = () => {
      const startTime = performance.now();

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Quadratic ease-out — much less "freeze" on small numbers than cubic
      const easeOut = 1 - Math.pow(1 - progress, 2);
      setCount(Math.round(easeOut * end));

        if (progress < 1) {
          frameRef.current = requestAnimationFrame(animate);
        }
      };

      frameRef.current = requestAnimationFrame(animate);
    };

    timer = setTimeout(runAnimation, delay);

    return () => {
      clearTimeout(timer);
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [trigger, end, duration, delay]);

  return { count };
}
